import { AnthropicToolSchema, GatewayTarget, GatewayTool, WizardShape } from '../services/agent-builder.interface';

/**
 * Plain-English presentation layer for the Abilities section.
 *
 * The gateway tool catalog (GET /tools) speaks in operationIds, JQL and SOQL —
 * fine for engineers, alien for the sales/marketing users the Studio is for.
 * This module maps each known tool onto a friendly "ability": a verb-first
 * name, a one-line description, an example question and a badge that says
 * what data it touches. Unknown tools (new catalog entries) fall back to a
 * generic ability derived from the tool's displayName + its target's info,
 * so the catalog never breaks when the gateway publishes something new.
 *
 * Wire names stay untouched: equipping an ability pushes the exact catalog
 * tool (name + input_schema) into WizardInput.tools for the renderer.
 */

export interface Ability {
  /** Unique catalog id (`target___operationId`) — or a builtin id. */
  id: string;
  /** Name stored in WizardInput.tools — the un-prefixed operationId the model
   *  calls (execute_tool adds the target prefix at runtime). */
  wireName?: string;
  kind: 'gateway' | 'builtin';
  /** True when the tool changes real-world state (books meetings, writes
   *  records) — surfaced as a "Takes real actions" warning in the catalog. */
  writeAction?: boolean;
  /** Gateway target the tool routes through (gateway abilities only). */
  target?: string;
  /** Friendly, verb-first name, e.g. "Look up deals & pipeline". */
  name: string;
  /** The source system, in plain words, e.g. "Salesforce". */
  system: string;
  desc: string;
  /** Example question a user could ask — shown as “Try: …”. */
  example?: string;
  /** What it touches, e.g. "Reads Salesforce". */
  badge: string;
  category: string;
  /** Optional sub-group within the connector (e.g. "BambooHR (HR)"); shown as
   *  a chip and used to section a grab-bag connector. "" when none. */
  group?: string;
  icon: string;
  popular?: boolean;
  /** Publisher-set display rank within the category (friendly.order) —
   *  ascending; unset/0 sorts last. See compareAbilities(). */
  order?: number;
  /** Underlying catalog tool (gateway abilities only). */
  tool?: GatewayTool;
}

/** Display order for catalog groups; categories with no entries are hidden.
 *  FALLBACK-ONLY: the server now owns this arrangement (GET /tools
 *  `presentation.category_order`) — this bundled copy applies only when the
 *  deployed studio-api predates the presentation contract. */
export const ABILITY_CATEGORIES = ['Salesforce', 'Finance', 'Licensing', 'Catsy', 'Knowledge', 'Documents', 'Communication', 'Outreach', 'Support', 'Web'];

/** Per-target fallbacks for tools without hand-written friendly metadata.
 *  FALLBACK-ONLY for catalogs published before the friendly back-port — the
 *  published `friendly` block wins on every field. Do NOT add new targets
 *  here; publish their friendly metadata with the gateway tools instead. */
const TARGET_INFO: Record<string, { system: string; category: string; badge: string; icon: string }> = {
  'salesforce-read': { system: 'Salesforce', category: 'Salesforce', badge: 'Reads Salesforce', icon: 'database' },
  'snowflake-read': { system: 'revenue data', category: 'Finance', badge: 'Reads revenue data', icon: 'chart' },
  'atlassian-read': { system: 'Jira & Confluence', category: 'Knowledge', badge: 'Reads Jira & Confluence', icon: 'book' },
  'chilipiper-read': { system: 'exec calendars', category: 'Communication', badge: 'Reads calendars', icon: 'clock' },
  'chilipiper-write': { system: 'exec calendars', category: 'Communication', badge: 'Books meetings', icon: 'clock' },
  'm365-read': { system: 'Microsoft 365', category: 'Documents', badge: 'Reads Microsoft 365', icon: 'mail' },
  'lems-read': { system: 'Licensing', category: 'Licensing', badge: 'Reads Licensing', icon: 'shield' },
  'lems-write': { system: 'Licensing', category: 'Licensing', badge: 'Writes to Licensing', icon: 'shield' },
  'outreach-read': { system: 'Outreach', category: 'Outreach', badge: 'Reads Outreach', icon: 'mail' },
  'outreach-write': { system: 'Outreach', category: 'Outreach', badge: 'Writes Outreach', icon: 'mail' },
  'catsy-read': { system: 'Catsy', category: 'Catsy', badge: 'Reads Catsy', icon: 'database' },
};

const DEFAULT_TARGET_INFO = { system: 'an internal system', category: 'Knowledge', badge: 'Reads internal data', icon: 'wrench' };

export function targetInfo(target: string): { system: string; category: string; badge: string; icon: string } {
  return TARGET_INFO[target] ?? DEFAULT_TARGET_INFO;
}

type FriendlyMeta = Partial<Pick<Ability, 'name' | 'system' | 'desc' | 'example' | 'category' | 'icon' | 'popular' | 'badge'>>;

/**
 * Hand-written friendly metadata for the published gateway tools, keyed by
 * wire name. Mirrors wg-agents-gateways/tool-lambdas/<target>/tool-schema.json
 * as of 2026-06 — entries missing here just render with the generic fallback.
 *
 * FALLBACK-ONLY for catalogs published before the friendly back-port: the
 * `friendly` block published with the tool wins on every field. New tools
 * must NOT be added here — publish their friendly metadata with the tool.
 */
const FRIENDLY_TOOL_META: Record<string, FriendlyMeta> = {
  // ── salesforce-read ──────────────────────────────────────────────
  'salesforce-read___query_accounts': {
    name: 'Look up customer accounts',
    desc: 'Finds account details like industry, size and location.',
    example: 'Tell me about the Acme account.',
    popular: true,
  },
  'salesforce-read___query_contacts': {
    name: 'Find customer contacts',
    desc: 'Looks up people at an account — names, titles and emails.',
    example: 'Who are our contacts at Acme?',
  },
  'salesforce-read___query_assets': {
    name: 'Check products a customer owns',
    desc: 'Lists the products, licenses and devices an account owns.',
    example: 'What products does Acme own?',
  },
  'salesforce-read___query_opportunities': {
    name: 'Look up deals & pipeline',
    desc: 'Checks open and past deals — stage, amount and close dates.',
    example: 'What open deals do we have with Acme?',
    popular: true,
  },
  'salesforce-read___query_cases': {
    name: 'Check support cases',
    desc: 'Looks up open and recent support cases for a customer.',
    example: 'Any open support cases for Acme?',
    category: 'Support',
    icon: 'alert',
  },
  'salesforce-read___query_tasks': {
    name: 'See recent account activity',
    desc: 'Finds recent calls, emails and to-dos logged for an account.',
    example: "What's the latest activity on Acme?",
  },
  'salesforce-read___query_events': {
    name: 'Check scheduled meetings',
    desc: 'Finds upcoming or recent meetings booked with an account.',
    example: 'When is our next meeting with Acme?',
  },
  'salesforce-read___query_sales_plays': {
    name: 'Check open sales plays',
    desc: 'Finds active sales plays running for an account.',
    example: 'Which sales plays are open for Acme?',
  },
  'salesforce-read___count_records': {
    name: 'Count things in Salesforce',
    desc: "Answers 'how many' questions across accounts, deals and more.",
    example: 'How many open deals do we have this quarter?',
  },
  // ── snowflake-read ───────────────────────────────────────────────
  'snowflake-read___query_partner_revenue': {
    name: 'Check partner revenue',
    desc: 'Pulls partner sales and revenue numbers.',
    example: 'Top 10 partners by revenue this quarter?',
    popular: true,
  },
  'snowflake-read___search_win_notes': {
    name: 'Search win/loss notes',
    desc: 'Searches the notes on closed deals for a keyword.',
    example: 'Find wins that mention MFA.',
  },
  'snowflake-read___list_wins': {
    name: 'List closed deals',
    desc: 'Lists won or lost deals, filtered by product, region or partner.',
    example: "Show me last quarter's wins in EMEA.",
  },
  'snowflake-read___count_wins': {
    name: 'Count wins & revenue',
    desc: 'Counts closed deals and adds up their revenue.',
    example: 'How many deals did we win in Q2?',
  },
  'snowflake-read___win_breakdown': {
    name: 'Break down wins by category',
    desc: 'Groups won deals by product, region, partner and more.',
    example: 'Break down Q2 wins by product.',
  },
  // ── atlassian-read ───────────────────────────────────────────────
  'atlassian-read___search_jira_issues': {
    name: 'Search Jira tickets',
    desc: 'Finds Jira tickets by project, owner or status.',
    example: "What's open against the Firebox 12.11 release?",
    popular: true,
  },
  'atlassian-read___run_jql': {
    name: 'Run an advanced Jira search',
    desc: 'For precise Jira searches written in JQL.',
    example: 'Run my saved JQL for stale bugs.',
  },
  'atlassian-read___get_jira_issue': {
    name: 'Open a specific Jira ticket',
    desc: "Pulls up one ticket's full details by its ID.",
    example: 'Show me PROJ-123.',
  },
  'atlassian-read___get_jira_issue_comments': {
    name: 'Read ticket comments',
    desc: 'Reads the discussion on a Jira ticket.',
    example: "What's the latest comment on PROJ-123?",
  },
  'atlassian-read___search_jira_users': {
    name: 'Find people in Jira',
    desc: 'Looks up colleagues in Jira by name or email.',
    example: 'Find Maria Chen in Jira.',
  },
  'atlassian-read___get_jira_user': {
    name: 'Look up a Jira profile',
    desc: "Fetches one person's Jira profile.",
    example: "Show me Maria's Jira profile.",
  },
  'atlassian-read___search_confluence': {
    name: 'Search Confluence pages',
    desc: 'Finds pages and docs across Confluence.',
    example: 'Find our onboarding guide.',
    popular: true,
  },
  'atlassian-read___run_cql': {
    name: 'Run an advanced Confluence search',
    desc: 'For precise Confluence searches written in CQL.',
    example: 'Run a CQL search for release notes.',
  },
  'atlassian-read___get_confluence_page': {
    name: 'Open a Confluence page',
    desc: "Pulls up one page's content.",
    example: 'Open the Q3 planning page.',
  },
  'atlassian-read___list_confluence_page_children': {
    name: "Browse a page's sub-pages",
    desc: 'Lists the pages nested under a Confluence page.',
    example: "What's under the Sales Playbook page?",
  },
  // ── chilipiper-read / chilipiper-write ───────────────────────────
  'chilipiper-read___cp_find_user': {
    name: "Find an exec's booking profile",
    desc: "Looks up an executive's meeting-booking profile.",
    example: "Find the CRO's booking profile.",
  },
  'chilipiper-read___cp_get_availability': {
    name: 'Check exec availability',
    desc: "Finds open time slots on an executive's calendar.",
    example: 'When is the CTO free next week?',
    popular: true,
  },
  'chilipiper-read___cp_get_meeting': {
    name: 'Look up a booked meeting',
    desc: 'Checks the details of a scheduled meeting.',
    example: "What's the status of the Acme exec meeting?",
  },
  'chilipiper-write___cp_init_scheduling_link': {
    name: 'Start a meeting booking',
    desc: "Prepares a booking link for an executive's meeting type.",
    example: 'Set up a booking with the CRO.',
  },
  'chilipiper-write___cp_book_meeting': {
    name: 'Book a meeting',
    desc: 'Books the meeting on the calendar and sends invites.',
    example: 'Book the 3pm slot on Tuesday.',
  },
  'chilipiper-write___cp_cancel_meeting': {
    name: 'Cancel a meeting',
    desc: 'Cancels a booked meeting and notifies attendees.',
    example: 'Cancel the Acme intro call.',
  },
  // ── lems-write ───────────────────────────────────────────────────
  'lems-write___create_orders': {
    name: 'Create device extension orders',
    desc: 'Creates the Boomi orders needed to extend device licenses.',
    example: 'Create extension orders for serials ABC123 and DEF456.',
    category: 'Licensing',
    icon: 'shield',
  },
  'lems-write___activate_devices': {
    name: 'Activate devices',
    desc: 'Submits bulk device activations to LEMS.',
    example: 'Activate device ABC123 with activation key XYZ.',
    category: 'Licensing',
    icon: 'shield',
  },
  'lems-write___submit_saas_extension': {
    name: 'Extend SaaS licenses',
    desc: 'Submits bulk SaaS license extensions to LEMS.',
    example: 'Extend the SaaS license for container key ABC-789.',
    category: 'Licensing',
    icon: 'shield',
  },
  'lems-write___get_batch_status': {
    name: 'Check activation batch status',
    desc: 'Checks the processing status of a bulk activation or extension batch.',
    example: "What's the status of batch ID 12345?",
    category: 'Licensing',
    icon: 'shield',
  },
  // ── catsy-read ───────────────────────────────────────────────────
  'catsy-read___get_products_by_sku': {
    name: 'Look up product details by SKU',
    desc: 'Gets product family, hardware grouping and product name for a list of SKUs.',
    example: 'Give me the product family and hardware grouping for SKUs WGAP13000000 and WGT81000.',
    category: 'Catsy',
    icon: 'database',
    popular: true,
  },
  'catsy-read___list_product_attributes': {
    name: 'List Catsy product attributes',
    desc: 'Lists the available Catsy product attributes and their keys.',
    example: "What Catsy attributes contain the word 'family'?",
    category: 'Catsy',
    icon: 'database',
  },
};

/** Abilities that are shape flags rather than gateway tools. */
export const BUILTIN_ABILITIES: Ability[] = [
  {
    id: '__report_artifact',
    kind: 'builtin',
    name: 'Create downloadable reports',
    system: 'Report builder',
    desc: 'Turns an answer into a polished report people can download.',
    example: 'Build a deal review report for Acme.',
    badge: 'Creates files',
    category: 'Documents',
    icon: 'doc',
    popular: true,
  },
  {
    id: '__file_upload',
    kind: 'builtin',
    name: 'Accept file uploads',
    system: 'File intake',
    desc: 'Lets people attach a file (PDF, HTML, text) for the agent to read.',
    example: 'Summarize this contract I uploaded.',
    badge: 'Reads files',
    category: 'Documents',
    icon: 'paperclip',
  },
  {
    // Flag-only builtin: toggling sets shape.has_knowledge_base and reveals
    // the wizard's "Knowledge base" step. The knowledge-read tool is injected
    // server-side at render time — NEVER pushed into WizardInput.tools here.
    id: '__knowledge_base',
    kind: 'builtin',
    name: 'Answer from your documents',
    system: 'Knowledge base',
    desc: 'Upload documents this agent answers from, with citations.',
    example: 'What does our travel policy say about upgrades?',
    badge: 'Reads your documents',
    category: 'Knowledge',
    icon: 'book',
  },
];

/** Maps each builtin ability id to the shape flag it toggles. */
export const BUILTIN_SHAPE_FLAG: Record<string, keyof WizardShape> = {
  __report_artifact: 'has_report_artifact',
  __file_upload: 'has_file_upload',
  __knowledge_base: 'has_knowledge_base',
};

// ── Knowledge-base creator grant ──────────────────────────────────────
// Admins grant "Search knowledge base" (gateway target `knowledge-read`,
// tool `search_knowledge_base`) per user/group in the admin ability grid.
// GET /tools is scope-filtered per caller when ability enforcement is on,
// so the target's presence in the catalog IS the creator grant — it gates
// whether the __knowledge_base builtin can be equipped at all.

export const KB_GRANT_TARGET = 'knowledge-read';
export const KB_GRANT_TOOL = 'search_knowledge_base';

/** True when the caller may CREATE knowledge-base agents.
 *
 *  `serverGranted` is the AUTHORITATIVE answer from GET /tools
 *  (`knowledge_base_granted`, computed by the same usability gate the KB
 *  upload/test-run enforces) — when provided it WINS, so the picker/library
 *  match runtime exactly. Prefer passing it.
 *
 *  Legacy fallback (flag absent — older API / offline mock): infer from the
 *  catalog carrying the `knowledge-read` target / `search_knowledge_base`
 *  tool. An EMPTY catalog falls open here, which is why the explicit flag is
 *  needed: an empty catalog is ALSO a no-grant user under enforcement, and
 *  inferring "granted" there is exactly what showed the KB card to users who
 *  then got a `kb_not_granted` 403 at test-run. */
export function knowledgeBaseGranted(
  catalog: GatewayTarget[],
  serverGranted?: boolean,
): boolean {
  if (serverGranted !== undefined) return serverGranted;
  if (catalog.length === 0) return true;
  return catalog.some(
    (c) =>
      c.target === KB_GRANT_TARGET ||
      c.tools.some(
        (t) =>
          (t.operationId || t.name) === KB_GRANT_TOOL ||
          t.name.endsWith(`___${KB_GRANT_TOOL}`),
      ),
  );
}

/** The catalog minus targets that must never surface as picker cards: the
 *  knowledge-read tool is the transport behind the __knowledge_base builtin
 *  (injected server-side at render time — never pushed into
 *  WizardInput.tools) and sits in the catalog purely as the creator-grant
 *  signal, so equipping it directly would duplicate the builtin without its
 *  Knowledge base step. */
export function equippableCatalog(catalog: GatewayTarget[]): GatewayTarget[] {
  return catalog.filter((c) => c.target !== KB_GRANT_TARGET);
}

function humanize(operationId: string): string {
  const words = operationId.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Sensitivity values meaning the tool changes real-world state. */
const WRITE_SENSITIVITIES = new Set(['write', 'sensitive', 'high']);

/**
 * Equipping abilities IS the tools decision — derive the renderer shape flags
 * from the equipped tools instead of asking the user to manage them.
 *
 * Pure: mutates nothing. `wireTargets` maps wire name (un-prefixed
 * operationId) → gateway target for every catalog tool; tools not in the map
 * are custom. Targets may mix across the set — each gateway tool carries its
 * own `target`; `gateway_target` stays as the fallback for entries without
 * one (legacy drafts, custom tools), so it must never be repointed while any
 * such entry is equipped. `fallbackTarget` covers the custom-tools-only case
 * (they still route through a gateway target).
 */
export function deriveShapeFromTools(
  shape: WizardShape,
  tools: AnthropicToolSchema[],
  wireTargets: Map<string, string>,
  fallbackTarget: string | null,
): WizardShape {
  const hasAnyTools = tools.length > 0;
  const next: WizardShape = { ...shape, has_tools: hasAnyTools, tools_via_gateway: hasAnyTools };
  const targets = new Set(
    tools.map((t) => t.target ?? wireTargets.get(t.name)).filter((t): t is string => !!t),
  );
  const hasUnrouted = tools.some((t) => !t.target && !wireTargets.has(t.name));
  if (!hasAnyTools) {
    next.gateway_target = null;
  } else if (hasUnrouted && next.gateway_target) {
    // keep the fallback the unrouted entries rely on
  } else if (targets.size === 1) {
    next.gateway_target = [...targets][0];
  } else if (targets.size === 0 && !next.gateway_target) {
    next.gateway_target = fallbackTarget;
  } else if (targets.size > 1 && (!next.gateway_target || !targets.has(next.gateway_target))) {
    next.gateway_target = [...targets].sort()[0];
  }
  return next;
}

/** Build the friendly ability for a catalog tool.
 *
 *  Source priority — PER FIELD, published value always first: (1) the
 *  `friendly` block published with the tool by its gateway team (single
 *  source of truth, travels with new tools), (2) this file's hand-written
 *  FRIENDLY_TOOL_META, (3) the per-target TARGET_INFO / generic defaults. */
export function abilityForGatewayTool(target: string, tool: GatewayTool): Ability {
  const info = targetInfo(target);
  const meta = FRIENDLY_TOOL_META[tool.name] ?? {};
  const pub = tool.friendly ?? {};
  return {
    id: tool.name,
    wireName: tool.operationId || tool.name,
    kind: 'gateway',
    target,
    writeAction: WRITE_SENSITIVITIES.has(tool.sensitivity ?? '') || target.endsWith('-write'),
    name: pub.name ?? meta.name ?? tool.displayName ?? humanize(tool.operationId || tool.name),
    system: pub.system ?? meta.system ?? info.system,
    desc: pub.description ?? meta.desc ?? tool.description,
    example: pub.example ?? meta.example,
    badge: pub.badge ?? meta.badge ?? info.badge,
    category: pub.category ?? meta.category ?? info.category,
    group: pub.group ?? '',
    icon: pub.icon ?? meta.icon ?? info.icon,
    popular: pub.popular ?? meta.popular,
    order: pub.order,
    tool,
  };
}

/** Display sort within a category: publisher-set friendly `order` ascending
 *  with unset/0 last, then name — the server presentation contract's
 *  sort_within_category = ['order', 'name']. */
export function compareAbilities(a: Ability, b: Ability): number {
  const ao = a.order || Number.MAX_SAFE_INTEGER;
  const bo = b.order || Number.MAX_SAFE_INTEGER;
  return ao !== bo ? ao - bo : a.name.localeCompare(b.name);
}
